import { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { WebauthPopupPage } from '@adapters/navigation/page';
import { Option } from '@core/model/framework/Result/Option';
import { PromiseTimedOut, promiseWithTimeout } from '@core/model/framework/promiseWithTimeout';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { assert } from 'ts-essentials';

export class WebauthPopupVM {
  private HAS_USER_STOPPED_WAITING_CHECK_TIMEOUT_IN_MS = 5 * 1000;

  public webauthPopupActualPage: Option<WebauthPopupPage>;

  constructor(
    private saveBankConnectionUsecase: SaveWebauthBankConnection,
    private browserBroadcast: BrowserBroadcast
  ) {
    this.webauthPopupActualPage = null;
  }

  async onInit(webauthPopupConnectionId: string | undefined, webauthPopupUserToken: string | undefined) {
    this.browserBroadcast.publish({
      label: 'WEBAUTH_POPUP_REACHED',
      data: null,
    });

    const isWebauthCancelled = await this.hasUserStoppedWaiting();
    if (isWebauthCancelled) {
      console.log(`WebauthPopup: User has stopped waiting for webauth.`);
      this.dispatchWebauthFail();
      return;
    }

    if (this.isExternalBankConnectionSucceeded(webauthPopupConnectionId, webauthPopupUserToken)) {
      assert(webauthPopupConnectionId != null && webauthPopupUserToken != null);
      await this.saveBankConnection(webauthPopupUserToken, webauthPopupConnectionId);
    } else {
      this.dispatchWebauthFail();
    }
  }

  // User can go back to the bank choice or maybe have two webauth tabs, and complete both.
  // In either of these cases, we do not want to save the connection and we will present
  // The fail screen.
  private async hasUserStoppedWaiting(): Promise<boolean> {
    const promise = () =>
      firstValueFrom(
        this.browserBroadcast.getMessageFromLabel('WEBAUTH_POPUP_ANSWER_HAS_USER_STOPPED_WAITING')
      );

    try {
      const response = await promiseWithTimeout(promise(), this.HAS_USER_STOPPED_WAITING_CHECK_TIMEOUT_IN_MS);
      return response.data.hasUserStoppedWaiting;
    } catch (e) {
      if (e instanceof PromiseTimedOut) {
        return false;
      }
      throw e;
    }
  }

  private isExternalBankConnectionSucceeded(
    webauthPopupConnectionId: string | undefined,
    webauthPopupUserToken: string | undefined
  ): boolean {
    return webauthPopupConnectionId != null && webauthPopupUserToken != null;
  }

  private async saveBankConnection(webauthPopupUserToken: string, webauthPopupConnectionId: string) {
    const result = await this.saveBankConnectionUsecase.execute({
      webauthPopupUserToken,
      webauthPopupConnectionId,
    });

    if (result.success === true) {
      this.dispatchWebauthSuccess();
    } else {
      this.dispatchWebauthFail();
    }
  }

  private dispatchWebauthSuccess() {
    this.webauthPopupActualPage = { value: 'CONNECTION_EXTERNAL_BANK_OK' };
  }

  private dispatchWebauthFail() {
    this.webauthPopupActualPage = { value: 'CONNECTION_EXTERNAL_BANK_KO' };
    this.browserBroadcast.publish({ label: 'CONNECTION_EXTERNAL_BANK_KO', data: null });
  }
}
